'use client';

import { useId, useMemo, useState } from 'react';
import { FileText, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CertifiedCopyBadge } from '@/components/ui/certified-copy-badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { MatterFile } from '@/lib/services/files/types';

interface AttachmentSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The documents available to attach, resolved by the caller.
   *
   * Passed in rather than fetched here (LF-6023) because the source depends on the
   * thread's owner: a matter's Documents tray, or an enquiry's scanned client
   * documents. The picker hook knows the owner and already holds the list for its
   * own chip labels, so lifting the read also removes a second fetch of the same
   * data.
   */
  files: MatterFile[];
  /**
   * What the existing-documents list is a list *of*, in the user's words.
   *
   * **Plural** ("Matter documents", "Enquiry documents") — it names a list, and
   * both surfaces that render it read it as one: the sr-only dialog description
   * and the search placeholder. A singular label would read wrong in both
   * (LF-6286).
   */
  documentsLabel: string;
  /** File IDs already attached — pre-checked, can't double-add. */
  alreadySelected: number[];
  onAdd: (fileIds: number[]) => void;
}

/**
 * Dialog-based **choose-existing** picker: search + grouped checkbox list (core
 * documents first — death certificate, will, grant, IDs — then additional and
 * uploads). Certified copies show a badge so the lawyer can pick the right
 * version. On a matter these are the matter's documents; on an enquiry, the
 * client documents scanned onto it.
 *
 * This is the shared "Pick existing" sub-part of {@link AddDocument} (LF-6189):
 * upload lives on `AddDocument`'s FileDropZone Upload source, so this dialog no
 * longer carries an Upload tab (the legacy `DropZone` it used to embed is retired
 * from every "pick existing" surface).
 *
 * Hidden file types: `correspondence-attachment` (those are attachments on
 * *other* emails, not standalone matter docs) and infrastructure types
 * (letterhead, signatures, form templates).
 */

type FileTypeKey = string;

const FILE_TYPE_LABELS: Record<string, string> = {
  'death-certificate': 'Death certificate',
  will: 'Will',
  'grant-of-representation': 'Grant of representation',
  'primary-id': 'Primary ID',
  'secondary-id': 'Secondary ID',
  'institution-form-signed': 'Institution forms',
  'additional-document': 'Additional documents',
  'matter-document': 'Matter documents',
  'unknown-pending-ai-analysis': 'Pending AI classification'
};

// Sort order — core estate documents first, additional / catch-all last.
const FILE_TYPE_PRIORITY: Record<string, number> = {
  'death-certificate': 0,
  will: 1,
  'grant-of-representation': 2,
  'primary-id': 3,
  'secondary-id': 4,
  'institution-form-signed': 5,
  'matter-document': 9,
  'additional-document': 10,
  'unknown-pending-ai-analysis': 11
};

// File types that don't make sense as email attachments — hide from picker.
const HIDDEN_TYPES = new Set<string>([
  'correspondence-attachment',
  'letterhead',
  'handwritten-signature',
  'email-signature',
  'institution-form-template'
]);

const fileTypeKey = (f: MatterFile): FileTypeKey =>
  typeof f.fileType === 'string' ? f.fileType : f.fileType.slug;

const fileTypeLabel = (key: FileTypeKey): string =>
  FILE_TYPE_LABELS[key] ??
  key
    .split('-')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');

export function AttachmentSelectionDialog({
  open,
  onOpenChange,
  files,
  documentsLabel,
  alreadySelected,
  onAdd
}: AttachmentSelectionDialogProps) {
  const [search, setSearch] = useState('');
  const [pendingIds, setPendingIds] = useState<Set<number>>(() => new Set());
  const attachmentDialogId = useId();

  // Reset state each time the dialog opens so a stale selection doesn't
  // bleed across opens.
  const handleOpenChange = (next: boolean) => {
    if (next) {
      setPendingIds(new Set());
      setSearch('');
    }
    onOpenChange(next);
  };

  const grouped = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = files.filter((f) => {
      const key = fileTypeKey(f);
      if (HIDDEN_TYPES.has(key)) return false;
      if (term.length === 0) return true;
      const label = (f.displayName ?? f.originalFilename ?? '').toLowerCase();
      return label.includes(term);
    });

    const byType = new Map<FileTypeKey, MatterFile[]>();
    for (const f of filtered) {
      const key = fileTypeKey(f);
      const arr = byType.get(key) ?? [];
      arr.push(f);
      byType.set(key, arr);
    }
    return Array.from(byType.entries()).sort(([a], [b]) => {
      const pa = FILE_TYPE_PRIORITY[a] ?? 50;
      const pb = FILE_TYPE_PRIORITY[b] ?? 50;
      if (pa !== pb) return pa - pb;
      return a.localeCompare(b);
    });
  }, [files, search]);

  const toggle = (id: number) => {
    setPendingIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirm = () => {
    onAdd(Array.from(pendingIds));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg gap-3 p-4">
        <DialogHeader className="space-y-0">
          <DialogTitle className="text-sm font-semibold">
            Add attachments
          </DialogTitle>
          <DialogDescription className="sr-only">
            Choose one or more {documentsLabel.toLowerCase()} to attach.
          </DialogDescription>
        </DialogHeader>

        {/*
          `min-w-0`: DialogContent is a CSS grid, and a grid item defaults to
          `min-width: auto` — so without this the long, unbroken document
          filenames below blow this item out to their min-content width, far
          past the modal card, at desktop widths (LF-4329).
        */}
        <div className="w-full min-w-0 space-y-2">
          <div className="relative">
            <Search
              className="text-foreground-subtle absolute top-2 left-2 h-3.5 w-3.5"
              strokeWidth={1.75}
              aria-hidden="true"
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${documentsLabel.toLowerCase()}…`}
              className="text-12px h-8 pl-7"
              autoFocus
            />
          </div>

          <div className="border-border max-h-[320px] overflow-y-auto rounded-md border">
            {grouped.length === 0 ? (
              <p className="text-12px text-muted-foreground px-3 py-8 text-center">
                {search ? 'No matches' : 'No documents on this matter yet'}
              </p>
            ) : (
              <div className="divide-border-subtle divide-y">
                {grouped.map(([typeKey, group]) => (
                  <section key={typeKey} className="py-1.5">
                    <h3 className="text-12px text-muted-foreground px-3 pb-1 font-semibold tracking-wide uppercase">
                      {fileTypeLabel(typeKey)}
                    </h3>
                    <ul>
                      {group.map((file) => {
                        const isAlreadyAdded = alreadySelected.includes(
                          file.id
                        );
                        const isChecked =
                          isAlreadyAdded || pendingIds.has(file.id);
                        const inputId = `${attachmentDialogId}-${file.id}`;
                        return (
                          <li key={file.id}>
                            <label
                              htmlFor={inputId}
                              className={cn(
                                'flex items-center gap-2 px-3 py-1.5',
                                isAlreadyAdded
                                  ? 'cursor-default opacity-60'
                                  : 'hover:bg-surface-subtle cursor-pointer'
                              )}
                            >
                              <Checkbox
                                id={inputId}
                                checked={isChecked}
                                disabled={isAlreadyAdded}
                                onCheckedChange={() =>
                                  !isAlreadyAdded && toggle(file.id)
                                }
                                className="h-3.5 w-3.5"
                              />
                              <FileText
                                className="text-foreground-subtle h-3.5 w-3.5 shrink-0"
                                strokeWidth={1.75}
                                aria-hidden="true"
                              />
                              <span className="text-12px text-foreground-secondary min-w-0 flex-1 truncate">
                                {file.displayName ?? file.originalFilename}
                              </span>
                              {file.isCertifiedCopy && (
                                <CertifiedCopyBadge
                                  compact
                                  className="ml-auto shrink-0"
                                />
                              )}
                              {isAlreadyAdded && !file.isCertifiedCopy && (
                                <span className="text-12px text-muted-foreground ml-auto shrink-0">
                                  Added
                                </span>
                              )}
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button
              density="compact"
              type="button"
              variant="ghost"
              size="xs"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              density="compact"
              gap="standard"
              type="button"
              size="xs"
              onClick={handleConfirm}
              disabled={pendingIds.size === 0}
            >
              Add
              {pendingIds.size > 0 ? ` (${pendingIds.size})` : ''}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
